import React, { createContext, isValidElement } from 'react';
import {
  GetPropsCommonOptions,
  useCombobox,
  UseComboboxGetInputPropsOptions,
  UseComboboxGetItemPropsOptions,
  UseComboboxGetMenuPropsOptions,
  UseComboboxGetToggleButtonPropsOptions,
  UseComboboxStateChange,
} from 'downshift';
import {
  BulletFillIcon,
  ChevronDownOutlineIcon,
  ChevronUpOutlineIcon,
  SearchOutlineIcon,
} from '@applyboard/ui-icons';
import { FieldCompatibleProps } from '../Field';
import { SrOnly } from '../SrOnly';
import { Text } from '../Text';
import { Intent, useFullTheme, IconType } from '~/design';
import { IconWrapper, InternalTextInput, OptionLabel, useLinkAs } from '~/internal';
import { assert, findChildrenOfType, useId, useSelectStyling } from '~/utils';
import { Box } from '../Box';
import { Flex } from '../Flex';
import {
  FormControlContainer,
  FormControlContainerProps,
  useFormControlProps,
} from '~/internal/FormControlContainer';
import { DismissableLayer } from '@radix-ui/react-dismissable-layer';
import { getWidthStyles, useFloatingMenu } from '~/utils/floatingUi';
import { useContextOrThrow } from '~/utils/useContextOrThrow';
import { Background } from '../../building-blocks/Background';

type OptionWithGroup =
  | (ComboboxOptionProps & { groupValue?: string })
  | (Omit<ComboboxOptionProps, 'value'> & { value: symbol; groupValue?: string });

interface ComboboxContextProps {
  ariaLabel: ComboboxProps['aria-label'];
  ariaLabelledby: ComboboxProps['aria-labelledby'];
  disabled: ComboboxProps['disabled'];
  filterOption?: (option: ComboboxOptionProps, query: string) => boolean;
  getInputProps: (
    options?: UseComboboxGetInputPropsOptions,
    otherOptions?: GetPropsCommonOptions,
  ) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  getItemProps: (
    options: UseComboboxGetItemPropsOptions<OptionWithGroup>,
  ) => Record<string, unknown>;
  getMenuProps: (
    options?: UseComboboxGetMenuPropsOptions,
    otherOptions?: GetPropsCommonOptions,
  ) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  getToggleButtonProps: (options?: UseComboboxGetToggleButtonPropsOptions) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  helpTextId: string;
  highlightedIndex: number | null;
  inputInsideMenu: boolean;
  inputValue: string;
  intent: ComboboxProps['intent'];
  invalid: ComboboxProps['invalid'];
  isOpen: boolean;
  label: ComboboxProps['label'];
  labelId: string;
  leadIcon: ComboboxProps['leadIcon'];
  loading: ComboboxProps['loading'];
  onInputValueChange: (value?: string) => void;
  openMenu: () => void;
  closeMenu: () => void;
  options: OptionWithGroup[];
  placeholder: ComboboxProps['placeholder'];
  placeholderBtnRef: React.RefObject<HTMLButtonElement>;
  required: ComboboxProps['required'];
  selectedItem?: OptionWithGroup | null;
  selectItem: (item: OptionWithGroup | null) => void;
  size: ComboboxProps['size'];
  type: ComboboxProps['type'];
  valueMode: ComboboxProps['valueMode'];
  highlightedOptionRef: React.MutableRefObject<HTMLElement | null>;
}

const ComboboxContext = React.createContext<ComboboxContextProps | null>(null);
const ComboboxOptionGroupValueContext = createContext<string | null>(null);

export interface ComboboxProps extends FieldCompatibleProps<string> {
  /** The aria-label of the Combobox */
  'aria-label'?: string;

  /** The aria-labelledby of the Combobox */
  'aria-labelledby'?: string;

  children: React.ReactNode;

  /** If true, the Combobox cannot be interacted with. */
  disabled?: boolean;

  /** Callback function to override the Combobox default filter behavior. The default behavior is to filter based on if the Combobox.Option's label starts with the input value. */
  filterOption?: (option: ComboboxOptionProps, query: string) => boolean;

  /** The intent of the Combobox. Maps to colour. */
  intent?: Intent;

  labelPosition?: FormControlContainerProps['labelPosition'];

  /** The icon that appears on the left side of the combobox. */
  leadIcon?: IconType;

  /** If true, the Combobox will show loading text when open. */
  loading?: boolean;

  /** How the dropdown menu will align with the trigger. `start` will align the start (left) of the
   * menu with the start of the trigger. `end` will align the end (right) of the menu with the end
   * of the trigger. `end` is useful when you have a Combobox at the right side of the screen.
   * (default: start) */
  menuAlignment?: 'start' | 'end';

  /** How the dropdown menu will grow horizontally. `fill` will restrict dropdown menu to expand up to the
   * same width from `Select` parent component (if bigger than min-width), while `hug` will allow dropdown
   * menu to grow as much as necessary to accomodate the longest `Select.Option` content in a single line
   * (default: fill) */
  menuWidth?: 'fill' | 'hug';

  /** Callback that is invoked  when a user types into the field */
  onSearch?: (query: string) => void;

  /** The placeholder of the Combobox. Placeholders are considered bad for accessibility, so it's *strongly encouraged* to avoid using this.
   * See this article for more details: https://www.smashingmagazine.com/2018/06/placeholder-attribute/ */
  placeholder?: string;

  placeholderVariant?: 'indicator' | 'truncate';

  /** The size of the Combobox. */
  size?: 'sm' | 'md' | 'lg';

  /**
   * When type is search, the Combobox will behave much like a search bar:
   * - The LeadIcon, if not provided, will default to `SearchOutlineIcon`
   * - The trigger will have a `x` icon to its right, when an option is chosen. Clicking on it will clear the Combobox/Search value
   * - The down facing chevron wont be available
   */
  type?: 'text' | 'search';

  /**
   * Deprecated: Use `valueMode` instead.
   * If set, the Combobox will throw a rendering error if an option is not found
   * with the specified value.  This can be useful to ensure we don't
   * misleadingly display an empty combobox to the user when we're  missing data
   *
   * It's recommended to set this flag to `true`
   */
  enforceNoMissingOption?: boolean;

  /*
   * `valueMode` controls what the `value` prop represents
   * - `input` - The `value` prop is the same as what is shown in the text field
   * - `select` - The `value` prop represents the last value selected from the list of options
   * - `select-strict` - The `value` prop represents the last value selected from the list of options.  If the passed `value` is not in the list of options, a rendering error is thrown.
   */
  valueMode?: 'input' | 'select' | 'select-strict';
}

/**
 * Combobox allows the user to select one option and search for an option by typing in the Combobox text input.
 * - If you want to allow selecting multiple options, use the MultiCombobox component.
 */
export function Combobox({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  children,
  disabled = false,
  filterOption,
  helpText,
  intent = 'secondary',
  invalid,
  label,
  labelPosition,
  leadIcon,
  loading = false,
  menuAlignment = 'start',
  menuWidth = 'fill',
  onBlur,
  onChange,
  onSearch,
  placeholder,
  placeholderVariant = 'truncate',
  required,
  size = 'sm',
  type = 'text',
  value,
  valueMode = 'select',
  enforceNoMissingOption,
}: ComboboxProps) {
  const theme = useFullTheme();
  const { borderRadiuses, elevations } = theme;
  const { floatingStyles, refs } = useFloatingMenu({ menuAlignment, offset: 2 });
  const placeholderBtnRef = React.useRef<HTMLButtonElement>(null);
  const placeholderBtnId = useId();
  const highlightedOptionRef = React.useRef<HTMLElement | null>(null);

  const numChildrenTotal = React.Children.toArray(children).filter(Boolean).length;
  const comboboxChildren = findChildrenOfType(children, [
    Combobox.Option,
    Combobox.OptionGroup,
    Combobox.SeeMoreButton,
  ]);

  if (numChildrenTotal !== comboboxChildren.length) {
    throw new Error(
      'Combobox only allows children of type Combobox.Option, Combobox.OptionGroup, or Combobox.SeeMoreButton',
    );
  }

  if (!label && !ariaLabel && !ariaLabelledby) {
    throw new Error('One of label, aria-label or aria-labelledby is required');
  }

  if (label && ariaLabelledby) {
    throw new Error('Combobox cannot have both label and aria-labelledby');
  }

  if (ariaLabel && ariaLabelledby) {
    throw new Error('Combobox cannot have both aria-label and aria-labelledby');
  }

  const options = extractOptionsFromChildren(children);
  if (checkHasTooManySeeMoreButtons(options)) {
    throw new Error('Combobox only allows one Combobox.SeeMoreButton per group');
  }

  if (valueMode === 'input') {
    assert(
      options.every(option => option.value === option.label),
      'When valueMode is set to "input", all child options must have the same options.label and option.value',
    );
  }

  const selectedIndex = options.findIndex(option => option.value === value);
  const valueFromSelectedIndex = selectedIndex >= 0 ? options[selectedIndex]?.label ?? '' : '';

  const [prevValue, setPrevValue] = React.useState(value);
  const [inputValue, setInputValue] = React.useState(
    valueMode === 'input' ? value : valueFromSelectedIndex,
  );

  const [selectedItem, setSelectedItem] = React.useState<OptionWithGroup | null>(
    selectedIndex >= 0 ? options[selectedIndex] : null,
  );

  if (value !== prevValue) {
    setInputValue(valueMode === 'input' ? value : valueFromSelectedIndex);
    setPrevValue(value);
  }

  const filteredOptions = options.filter(option =>
    validateOptionIncluded(option, inputValue, filterOption),
  );

  const { adjustedIntent, helpTextId, labelId } = useFormControlProps({
    disabled,
    intent,
    invalid,
  });
  const inputInsideMenu = placeholderVariant === 'indicator';

  const onInputValueChange = (newInputValue?: string) => {
    if (!newInputValue) {
      onChange('');
    }
    onSearch?.(newInputValue || '');
    setInputValue(newInputValue || '');

    if (valueMode === 'input') {
      onChange(newInputValue || '');
    }
  };

  if (value && !selectedItem && (valueMode === 'select-strict' || enforceNoMissingOption)) {
    throw new Error(`[Combobox] No option found with value "${value}"`);
  }

  const {
    closeMenu,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    isOpen,
    highlightedIndex,
    selectItem,
    openMenu,
  } = useCombobox<OptionWithGroup>({
    inputValue,
    selectedItem,
    items: filteredOptions,
    itemToString: (item: OptionWithGroup | null) => (item ? item.label : ''),
    onSelectedItemChange: (changes: UseComboboxStateChange<OptionWithGroup>) => {
      setInputValue(changes.selectedItem?.label || '');

      if (changes.selectedItem?.value !== value) {
        const newSelectedItem = changes.selectedItem;
        setSelectedItem(newSelectedItem ?? null);
        onChange(typeof newSelectedItem?.value === 'string' ? newSelectedItem.value : '');

        if (inputInsideMenu) {
          placeholderBtnRef?.current?.focus();
        }
      }
    },
    onStateChange: ({ inputValue, type }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          onSearch?.(inputValue || '');
          if (inputInsideMenu) {
            placeholderBtnRef.current?.focus();
          }
          break;
        default:
          break;
      }
    },
    isItemDisabled(item) {
      return !!item.disabled;
    },
  });

  return (
    <ComboboxContext.Provider
      value={{
        ariaLabel,
        ariaLabelledby,
        disabled,
        filterOption,
        getInputProps,
        getItemProps,
        getMenuProps,
        getToggleButtonProps,
        helpTextId,
        highlightedIndex,
        inputValue,
        intent: adjustedIntent,
        invalid,
        inputInsideMenu,
        isOpen,
        label,
        labelId,
        leadIcon,
        loading,
        onInputValueChange,
        openMenu,
        options: filteredOptions,
        placeholder,
        placeholderBtnRef,
        required,
        selectedItem,
        selectItem,
        size,
        type,
        valueMode,
        highlightedOptionRef,
        closeMenu,
      }}
    >
      <ComboboxOptionGroupValueContext.Provider value={null}>
        <FormControlContainer
          disabled={disabled}
          helpText={helpText}
          helpTextId={helpTextId}
          intent={intent}
          invalid={invalid}
          label={label}
          labelId={labelId}
          labelPosition={labelPosition}
          required={required}
          {...getLabelProps(inputInsideMenu ? { htmlFor: placeholderBtnId } : {})}
        >
          {/* This is fine to disable because we're reacting to blurs from child interactive elements */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            onBlur={e => {
              const nextFocusedEl = e.relatedTarget;
              const focusStillInFieldset =
                nextFocusedEl instanceof Element &&
                refs.reference.current &&
                refs.reference.current.contains(nextFocusedEl);
              if (!focusStillInFieldset) {
                onBlur?.();
              }
            }}
            ref={refs.setReference}
          >
            {inputInsideMenu ? (
              <ComboboxPlaceholder id={placeholderBtnId} ref={placeholderBtnRef} />
            ) : (
              <ComboboxInput />
            )}
          </div>
        </FormControlContainer>

        <DismissableLayer
          asChild
          onPointerDownOutside={e => e.preventDefault()}
          onFocusOutside={e => e.preventDefault()}
          onDismiss={closeMenu}
        >
          <Background
            background="bgRegular"
            css={{
              borderRadius: borderRadiuses.control,
              boxShadow: elevations.z1,
              display: isOpen ? 'block' : 'none',
              overflowX: 'auto',
              zIndex: theme.zIndex.menu,
              ...getWidthStyles(menuWidth, refs),
            }}
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              // Not sure why this is required, I can't find where the `pointerEvents` is being
              // set to `none`
              pointerEvents: isOpen ? 'all' : 'none',
            }}
          >
            {inputInsideMenu ? (
              <MenuWithInput>{children}</MenuWithInput>
            ) : (
              <ComboboxMenu>{children}</ComboboxMenu>
            )}
          </Background>
        </DismissableLayer>
      </ComboboxOptionGroupValueContext.Provider>
    </ComboboxContext.Provider>
  );
}

function ComboboxInput() {
  const context = React.useContext(ComboboxContext);
  if (!context) {
    throw new Error('`ComboboxInput` can only be rendered inside `Combobox`');
  }
  const {
    ariaLabel,
    ariaLabelledby,
    disabled,
    getInputProps,
    getToggleButtonProps,
    helpTextId,
    intent,
    inputInsideMenu,
    invalid,
    inputValue: value,
    labelId,
    leadIcon,
    onInputValueChange,
    openMenu,
    placeholder,
    required,
    selectItem,
    size,
    type,
    highlightedOptionRef,
    highlightedIndex,
  } = context;

  const hideChevron = inputInsideMenu || type === 'search';
  const LeadIcon =
    type === 'search' && !leadIcon ? SearchOutlineIcon : leadIcon ? leadIcon : undefined;
  const iconProps = {
    contrast: disabled ? 'mid' : 'high',
    intent,
  } as const;

  const { onKeyDown, ...inputProps } = getInputProps({
    'aria-labelledby': ariaLabelledby || labelId,
    disabled,
    // Fix for cursor jump bug.  This fix is recommended in the docs here: https://github.com/downshift-js/downshift/blob/master/src/hooks/useCombobox/README.md#inputvalue
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      onInputValueChange(event.target.value);
    },
  });

  const highlightedOption =
    typeof highlightedIndex === 'number' && highlightedIndex >= 0
      ? context.options[highlightedIndex]
      : null;

  return (
    <InternalTextInput
      {...inputProps}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && (highlightedOption?.href || isSeeMoreButton(highlightedOption))) {
          highlightedOptionRef.current?.click();
          return;
        }

        onKeyDown(e);
      }}
      aria-describedby={helpTextId}
      aria-label={ariaLabel}
      disabled={disabled}
      hideNativeSearchClear
      intent={intent}
      invalid={invalid}
      leadAdornment={LeadIcon && <LeadIcon intent={invalid ? 'negative' : intent} />}
      onClear={
        value
          ? () => {
              selectItem(null);
              onInputValueChange('');
            }
          : undefined
      }
      onFocus={openMenu} // TODO: is this needed since v7 upgrade?
      placeholder={placeholder}
      required={required}
      size={size}
      trailAdornment={
        !hideChevron && (
          <button
            css={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              backgroundColor: 'transparent',
              borderStyle: 'none',
              lineHeight: 0,
            }}
            disabled={disabled}
            type="button"
            {...getToggleButtonProps({ disabled })}
            aria-label={context.isOpen ? 'Hide combo box options' : 'View combo box options'}
          >
            {context.isOpen ? (
              <ChevronUpOutlineIcon {...iconProps} />
            ) : (
              <ChevronDownOutlineIcon {...iconProps} />
            )}
          </button>
        )
      }
      type={type}
    />
  );
}

function ComboboxMenu({ children }: { children: ComboboxProps['children'] }) {
  const { labelId, getMenuProps, loading, options, inputValue, valueMode } = useContextOrThrow(
    ComboboxContext,
    '`ComboboxMenu` can only be rendered inside `Combobox`',
  );

  return (
    <ul
      css={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
      }}
      {...getMenuProps({
        'aria-labelledby': labelId,
      })}
    >
      {loading ? (
        <PlaceholderItem>Loading...</PlaceholderItem>
      ) : options.length || !inputValue ? (
        children
      ) : valueMode !== 'input' ? (
        <PlaceholderItem>No results found.</PlaceholderItem>
      ) : null}
    </ul>
  );
}

export interface ComboboxOptionsGroupProps {
  children: React.ReactNode;
  groupValue: string;
  label: string;
}

Combobox.OptionGroup = function ComboboxOptionGroup({
  children,
  groupValue,
  label,
}: ComboboxOptionsGroupProps) {
  const comboboxContext = React.useContext(ComboboxContext);
  const theme = useFullTheme();
  const groupStyling = useTextItemStyling();
  const { colors } = theme;

  if (!comboboxContext) {
    throw new Error('`Combobox.OptionGroup` can only be rendered inside `Combobox`');
  }

  if (
    React.Children.toArray(children).some(
      child =>
        isValidElement(child) &&
        child.type !== Combobox.Option &&
        child.type !== Combobox.SeeMoreButton,
    )
  ) {
    throw new Error(
      'Combobox.OptionGroup only allows children of type Combobox.Option or Combobox.SeeMoreButton',
    );
  }

  // Visible if there is at least one option in its group that is visible.
  const visible = comboboxContext.options.some(
    option =>
      option.groupValue === groupValue &&
      validateOptionIncluded(option, comboboxContext.inputValue, comboboxContext.filterOption),
  );

  if (visible) {
    return (
      <ComboboxOptionGroupValueContext.Provider value={groupValue}>
        <li
          css={{
            ...groupStyling,
            '&:nth-of-type(n+2)': {
              borderTop: `1px solid ${colors.borderInactive}`,
            },
          }}
        >
          <Text variant="bodyS">{label}</Text>
        </li>
        {children}
      </ComboboxOptionGroupValueContext.Provider>
    );
  }
  return null;
};

export interface ComboboxOptionProps {
  disabled?: boolean;
  label: string;
  leadIcon?: IconType;
  value: string;
  href?: string;
}

Combobox.SeeMoreButton = function ComboboxSeeMoreButton({
  onClick,
  label = 'See more results',
}: {
  onClick: () => void;
  label?: string;
}) {
  const theme = useFullTheme();
  const { colors, spacing } = theme;
  const { options, getItemProps, highlightedIndex, highlightedOptionRef } = useContextOrThrow(
    ComboboxContext,
    '`Combobox.SeeMoreButton` can only be rendered inside `Combobox`',
  );
  const groupValue = React.useContext(ComboboxOptionGroupValueContext);

  // Determines if the this option should be highlighted
  const currentOption = options.find(
    option => groupValue === (option.groupValue || null) && isSeeMoreButton(option),
  );
  assert(currentOption, 'Combobox.SeeMoreButton must be rendered inside Combobox');

  const currentIndex = currentOption ? options.indexOf(currentOption) : -1;
  const isHighlighted = highlightedIndex === currentIndex;

  return (
    // eslint-disable-next-line -- keyboard interaction is handled by parent
    <li
      css={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing[2],
        paddingTop: spacing[3],
        paddingRight: spacing[4],
        paddingBottom: spacing[3],
        paddingLeft: spacing[4],
        listStyleType: 'none',
        background: isHighlighted ? colors.bgRegularHover : undefined,
        cursor: 'pointer',
        textDecoration: 'none',
      }}
      {...getItemProps({
        index: currentIndex,
        item: currentOption,
        ref: isHighlighted ? highlightedOptionRef : undefined,
      })}
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Text variant="bodyM" as="span">
        <span
          css={theme => ({
            color: theme.colors.textPrimary,
            textDecoration: 'underline',
            textDecorationThickness: isHighlighted ? '2px' : undefined,
          })}
        >
          {label}
        </span>
      </Text>
    </li>
  );
};

Combobox.Option = function ComboboxOption({
  disabled = false,
  label,
  leadIcon: LeadIcon,
  value,
  href,
}: ComboboxOptionProps) {
  const theme = useFullTheme();
  const { colors, spacing } = theme;
  const comboContext = React.useContext(ComboboxContext);
  const LinkAs = useLinkAs();

  if (!comboContext) {
    throw new Error('`Combobox.Option` can only be rendered inside `Combobox`');
  }

  const currentOption = comboContext.options.find(option => option.value === value);
  const visible = currentOption
    ? validateOptionIncluded(currentOption, comboContext.inputValue, comboContext.filterOption)
    : false;

  if (visible && currentOption) {
    const selected = comboContext.selectedItem ? comboContext.selectedItem.value === value : false;

    // Determines if the this option should be highlighted
    const currentIndex = comboContext.options.findIndex(
      option => option.value === currentOption.value,
    );
    const Tag = href ? LinkAs : 'li';
    const isHighlighted = comboContext.highlightedIndex === currentIndex;

    const { onClick, ...itemsProps } = comboContext.getItemProps({
      'aria-disabled': disabled ? true : undefined,
      'aria-selected': selected,
      index: currentIndex,
      item: currentOption,
      ref: isHighlighted ? comboContext.highlightedOptionRef : undefined,
    });

    return (
      <Tag
        {...(href ? { href } : {})}
        css={{
          display: visible ? 'flex' : 'none',
          alignItems: 'center',
          gap: spacing[2],
          paddingTop: spacing[3],
          paddingRight: spacing[4],
          paddingBottom: spacing[3],
          paddingLeft: spacing[4],
          listStyleType: 'none',
          background: selected
            ? colors.bgPrimary
            : isHighlighted
              ? colors.bgRegularHover
              : undefined,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textDecoration: 'none',
        }}
        {...itemsProps}
        // @ts-expect-error TODO: fix me
        onClick={e => {
          if (href) {
            // If opening in the same tab, close and clear the input
            if (checkLinkClickOpeningInThisTab(e, e.currentTarget.getAttribute('target'))) {
              comboContext.closeMenu();
              comboContext.onInputValueChange('');
            }
            e.stopPropagation();
            return;
          }

          if (typeof onClick === 'function') {
            onClick?.(e);
          }
        }}
      >
        {LeadIcon && <LeadIcon />}
        <Text
          contrast={disabled ? 'low' : undefined}
          intent={selected ? 'primary' : undefined}
          variant="bodyM"
        >
          <OptionLabel label={label} search={comboContext.inputValue} />
          <SrOnly>{label}</SrOnly>
        </Text>
      </Tag>
    );
  }
  return null;
};

function validateOptionIncluded(
  option: OptionWithGroup,
  inputValue: string | null,
  externalFilter?: (option: ComboboxOptionProps, query: string) => boolean,
) {
  if (isSeeMoreButton(option)) {
    return true;
  }

  return !inputValue
    ? true
    : externalFilter
      ? externalFilter(option, inputValue)
      : option.label.toLowerCase().startsWith(inputValue.toLowerCase());
}

function extractOptionsFromChildren(children: React.ReactNode): Array<OptionWithGroup> {
  const options = new Array<OptionWithGroup>();
  React.Children.toArray(children).forEach(child => {
    if (
      isValidElement(child) &&
      (child.type === Combobox.Option ||
        child.type === Combobox.OptionGroup ||
        child.type === Combobox.SeeMoreButton)
    ) {
      recursiveExtractOptionsFromChildren(child, options);
    }
  });
  return options;
}

const seeMoreSymbol = Symbol('see more options');
const recursiveExtractOptionsFromChildren = (
  child: React.ReactNode,
  options: Array<OptionWithGroup>,
  groupValue?: string,
): void => {
  const childReactElement = child;
  if (!isValidElement(childReactElement)) {
    return;
  }

  if (childReactElement.type === Combobox.OptionGroup) {
    React.Children.forEach(childReactElement.props.children, child => {
      recursiveExtractOptionsFromChildren(child, options, childReactElement.props.groupValue);
    });
  } else if (childReactElement.type === Combobox.Option) {
    options.push({
      disabled: childReactElement.props.disabled,
      label: childReactElement.props.label,
      value: childReactElement.props.value,
      href: childReactElement.props.href,
      groupValue,
    });
  } else if (childReactElement.type === Combobox.SeeMoreButton) {
    options.push({
      label: childReactElement.props.label,
      value: seeMoreSymbol,
      groupValue,
    });
  }
};

function PlaceholderItem({ children }: { children: string }) {
  const style = useTextItemStyling();
  return (
    <li css={style}>
      <Text variant="bodyS" contrast="low">
        {children}
      </Text>
    </li>
  );
}

function useTextItemStyling() {
  const { spacing } = useFullTheme();
  return {
    cursor: 'auto',
    display: 'list-item',
    listStyleType: 'none',
    paddingBottom: spacing[2],
    paddingLeft: spacing[4],
    paddingRight: spacing[4],
    paddingTop: spacing[3],
  };
}

function MenuWithInput({ children }: { children: ComboboxProps['children'] }) {
  const context = React.useContext(ComboboxContext);
  if (!context) {
    throw new Error('`MenuWithInput` can only be rendered inside `Combobox`');
  }
  return (
    <Flex direction="column" gap={2}>
      <Box px={2} pt={2}>
        <ComboboxInput />
      </Box>

      <ComboboxMenu>{children}</ComboboxMenu>
    </Flex>
  );
}

interface ComboboxPlaceholderProps {
  id: string;
}
const ComboboxPlaceholder = React.forwardRef<HTMLButtonElement, ComboboxPlaceholderProps>(
  function ComboboxPlaceholderInner({ id, ...props }, ref) {
    const context = React.useContext(ComboboxContext);
    const { spacing } = useFullTheme();
    if (!context) {
      throw new Error('`ComboboxPlaceholder` can only be rendered inside `Combobox`');
    }
    const {
      disabled,
      getToggleButtonProps,
      intent,
      invalid,
      placeholder: _placeholder,
      selectedItem,
      size,
    } = context;
    const placeholder = _placeholder || 'Select...';
    const buttonStyle = useSelectStyling({ emphasis: 'outlined', invalid, intent, size });

    return (
      <button
        css={[buttonStyle, { width: '100%' }]}
        {...getToggleButtonProps({ ...props, disabled, id, ref, tabIndex: 0 })}
        type="button"
      >
        <div
          css={{
            alignItems: 'center',
            display: 'flex',
            gap: spacing[1],
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {placeholder}
          {selectedItem && (
            <IconWrapper
              disabled={disabled}
              intent={disabled ? 'secondary' : 'primary'}
              selector="div"
            >
              <BulletFillIcon />
            </IconWrapper>
          )}
        </div>
      </button>
    );
  },
);

function isSeeMoreButton<T extends OptionWithGroup>(
  option: T | undefined | null,
): option is Extract<T, { value: symbol }> {
  return option?.value === seeMoreSymbol;
}

/**
 * Checks if there are too many see more buttons in the options
 *
 * More than one see more button per group is not allowed.
 * More than one see more button without a group is not allowed.
 */
function checkHasTooManySeeMoreButtons(options: OptionWithGroup[]) {
  const countsPerGroup = new Map<string | undefined, number>();

  for (const option of options) {
    if (isSeeMoreButton(option)) {
      countsPerGroup.set(option.groupValue, (countsPerGroup.get(option.groupValue) || 0) + 1);
    }
  }

  return Array.from(countsPerGroup.values()).some(count => count > 1);
}

/**
 * Checks if the link click is about to open the link in the same tab
 *
 * @note Taken from https://github.com/remix-run/react-router/blob/main/packages/react-router/lib/dom/dom.ts#L34-L43
 */
function checkLinkClickOpeningInThisTab(
  event: React.MouseEvent<HTMLElement, MouseEvent>,
  target?: string | null,
) {
  return (
    event.button === 0 && // Ignore everything but left clicks
    (!target || target === '_self') && // Let browser handle "target=_blank" etc.
    !isModifiedEvent(event) // Ignore clicks with modifier keys
  );
}

function isModifiedEvent(event: React.MouseEvent<HTMLElement, MouseEvent>) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}
